/**
 * PATH: packages/ui/src/components/global-search/SearchFacetRail.tsx
 * FILE_ID: UI-GLOBALSEARCH-FACETRAIL-001
 * PURPOSE: Right-hand rail of source "Pages" icons (All, Pages, Jobs,
 *          Emails, Kanban, …) with live result counts. Clicking a
 *          facet scopes the centre results to that source; "All"
 *          clears the scope. This is the structural half of the
 *          two-pane palette — the navigator on the right, the direct
 *          information in the centre.
 * WHY:     Operator (2026-06-04): "expand the search architecture
 *          where 'Pages' come in the right like icons and the actual
 *          direct information comes in the centre".
 * FLOW:
 *   Facet[] + activeFacet → vertical icon buttons → onSelect(key|null)
 * DEPENDENCIES:
 *   - lucide-react: facet icons (local map — not the limited getIcon)
 *   - ../../lib/utils (cn): class composition
 *   - ./facets (Facet, FacetKey): the data this rail renders
 */

import {
  Compass,
  Briefcase,
  Mail,
  LayoutGrid,
  FileText,
  File,
  User,
  ClipboardCheck,
  LayoutList,
} from 'lucide-react'
import { cn } from '../../lib/utils'
import type { Facet, FacetKey } from './facets'

/** Local icon resolver — the platform `getIcon` registry doesn't
 *  carry Compass / LayoutGrid, and we want the rail to own its
 *  visual language regardless of what providers ship. */
const FACET_ICON: Record<string, any> = {
  Compass,
  Briefcase,
  Mail,
  LayoutGrid,
  FileText,
  File,
  User,
  ClipboardCheck,
}

/** Colour token → text/border classes for the active pill + dot. */
const COLOR_CLASS: Record<string, string> = {
  primary: 'text-primary',
  teal: 'text-teal-600 dark:text-teal-400',
  blue: 'text-blue-600 dark:text-blue-400',
  orange: 'text-orange-600 dark:text-orange-400',
  green: 'text-green-600 dark:text-green-400',
  purple: 'text-purple-600 dark:text-purple-400',
  emerald: 'text-emerald-600 dark:text-emerald-400',
  red: 'text-red-600 dark:text-red-400',
  gray: 'text-muted-foreground',
}

interface SearchFacetRailProps {
  facets: Facet[]
  activeFacet: FacetKey | null
  totalCount: number
  onSelect: (facet: FacetKey | null) => void
}

function FacetButton({
  icon,
  label,
  count,
  color,
  isActive,
  onClick,
}: {
  icon: any
  label: string
  count: number
  color: string
  isActive: boolean
  onClick: () => void
}) {
  const Icon = icon
  const colorText = COLOR_CLASS[color] ?? COLOR_CLASS.gray
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      title={`${label} (${count})`}
      className={cn(
        'group relative w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors',
        isActive
          ? 'bg-card shadow-sm ring-1 ring-border'
          : 'hover:bg-card/70',
      )}
    >
      {/* Active accent bar */}
      <span
        className={cn(
          'absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full transition-opacity',
          isActive ? 'opacity-100 bg-primary' : 'opacity-0',
        )}
      />
      <span
        className={cn(
          'w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0',
          isActive ? colorText : 'text-muted-foreground group-hover:text-foreground',
        )}
      >
        <Icon className="w-4 h-4" />
      </span>
      {/* Phones show the rail icon-only (title carries the label);
          labels + counts return at sm. */}
      <span
        className={cn(
          'hidden flex-1 min-w-0 text-xs font-medium truncate sm:block',
          isActive ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          'hidden text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 tabular-nums sm:block',
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-muted-foreground',
        )}
      >
        {count}
      </span>
    </button>
  )
}

/**
 * The right-rail. Renders one button per present facet plus an
 * always-present "All" entry at the top. Hidden by the parent
 * when there are no results or no query.
 */
export function SearchFacetRail({
  facets,
  activeFacet,
  totalCount,
  onSelect,
}: SearchFacetRailProps) {
  return (
    <div className="w-16 flex-shrink-0 border-l border-border bg-muted/40 p-2 overflow-y-auto sm:w-44">
      <div className="hidden px-2.5 pt-1 pb-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider sm:block">
        Sources
      </div>
      <div className="space-y-0.5">
        <FacetButton
          icon={LayoutList}
          label="All"
          count={totalCount}
          color="primary"
          isActive={activeFacet === null}
          onClick={() => onSelect(null)}
        />
        {facets.map((facet) => (
          <FacetButton
            key={facet.key}
            icon={FACET_ICON[facet.icon] ?? File}
            label={facet.label}
            count={facet.count}
            color={facet.color}
            isActive={activeFacet === facet.key}
            onClick={() =>
              onSelect(activeFacet === facet.key ? null : facet.key)
            }
          />
        ))}
      </div>
      {/* Keyboard hint — the active source can be cycled without leaving
          the search input via Alt/Option + ↑/↓ (handled in useSearch). */}
      <div className="mt-2 hidden px-2.5 pt-2 border-t border-border/70 text-[10px] text-muted-foreground items-center gap-1 sm:flex">
        <kbd className="px-1 py-0.5 rounded bg-muted font-sans">⌥</kbd>
        <kbd className="px-1 py-0.5 rounded bg-muted font-sans">↑↓</kbd>
        <span>switch source</span>
      </div>
    </div>
  )
}
